import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Mail, ArrowRight, ArrowLeft, BookOpen, Shield } from 'lucide-react';

export default function AuthPage() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otpId, setOtpId] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/request-otp', { email: email.trim().toLowerCase() });
      setOtpId(res.data.otp_id);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code) => {
    if (code.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', { email: email.trim().toLowerCase(), code });
      login(res.data.token, res.data.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111] flex" data-testid="auth-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#F5C518] items-center justify-center p-12">
        <div className="text-center max-w-md">
          <img 
            src="https://customer-assets.emergentagent.com/job_requirement-app-4/artifacts/ebglgl95_Untangling%20AI%20-%20Matt%20Kesby.jpeg" 
            alt="Untangling AI Book Cover" 
            className="w-64 mx-auto rounded-xl shadow-2xl mb-8"
          />
          <h2 className="text-2xl font-bold text-black mb-2">Bonus Materials Hub</h2>
          <p className="text-black/60 text-sm">Access your exclusive companion resources for Untangling AI</p>
        </div>
      </div>

      {/* Right side - auth */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-in">
          {/* Brand Header - mobile */}
          <div className="text-center mb-8 lg:mb-10">
            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
              Untangling <span className="text-[#F5C518]">AI</span>
            </h1>
            <p className="text-[#888] text-sm">
              Sign in to access your book resources
            </p>
          </div>

          {/* Auth Card */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 sm:p-8">
            {step === 'email' ? (
              <form onSubmit={handleRequestOtp} data-testid="email-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[#F5C518]/10 rounded-lg">
                    <Mail className="w-5 h-5 text-[#F5C518]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Sign in with email</h2>
                    <p className="text-[#888] text-xs">We'll send a one-time code to verify</p>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#888] mb-1.5">Email address</label>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-[#111] border border-[#333] rounded-lg text-white placeholder:text-[#555] focus:outline-none focus:border-[#F5C518] focus:ring-1 focus:ring-[#F5C518]/30 transition-colors"
                    data-testid="email-input"
                  />
                </div>
                {error && <p className="text-red-400 text-sm mb-3" data-testid="auth-error">{error}</p>}
                <button
                  type="submit" disabled={loading || !email}
                  className="w-full py-3 bg-[#F5C518] hover:bg-[#D4A910] text-black font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="send-code-button"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <><span>Send Code</span><ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[#666] text-xs text-center mt-4 flex items-center justify-center gap-1.5">
                  <Shield className="w-3 h-3" /> No password required. Secure OTP verification.
                </p>
              </form>
            ) : (
              <div data-testid="otp-form">
                <button onClick={() => { setStep('email'); setError(''); setOtp(''); }} className="flex items-center gap-1 text-[#888] hover:text-white text-sm mb-4 transition-colors" data-testid="back-to-email">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[#F5C518]/10 rounded-lg">
                    <BookOpen className="w-5 h-5 text-[#F5C518]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Enter your code</h2>
                    <p className="text-[#888] text-xs">Sent to <span className="text-[#F5C518]">{email}</span></p>
                  </div>
                </div>
                <div className="flex justify-center mb-4 otp-input-group">
                  <InputOTP maxLength={6} value={otp} onChange={(v) => { setOtp(v); if (v.length === 6) handleVerifyOtp(v); }} data-testid="otp-input">
                    <InputOTPGroup className="gap-2">
                      {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl font-bold border-2 border-[#333] bg-[#111] text-[#F5C518] rounded-lg" />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {error && <p className="text-red-400 text-sm text-center mb-3" data-testid="auth-error">{error}</p>}
                {loading && <div className="flex justify-center"><div className="w-5 h-5 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" /></div>}
                <p className="text-[#666] text-xs text-center mt-3">Code expires in 10 minutes</p>
                <button onClick={handleRequestOtp} disabled={loading} className="w-full mt-3 py-2 text-[#F5C518] hover:text-[#D4A910] text-sm font-medium transition-colors disabled:opacity-50" data-testid="resend-code-button">
                  Resend code
                </button>
              </div>
            )}
          </div>

          <p className="text-center text-[#555] text-xs mt-6">
            By signing in, you agree to receive emails about Untangling AI resources.
          </p>
        </div>
      </div>
    </div>
  );
}
